import {useMemo, useState} from 'react';
import {ChevronDown, ChevronRight, Eye, EyeOff, MailWarning, Search} from 'lucide-react';
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {type ClassificationConfig} from '@/api/classificationConfigs';
import {useSetClassificationHidden} from '@/api/hooks';
import {cn} from '@/lib/utils';
import {getApiErrorMessage} from '@/api/client';
import {showToast} from '@/lib/toast';
import {TablePanel} from '@/components/ui/table-panel';
import {adminSection} from '@/lib/adminSections';
import {useDebounce} from '@/lib/useDebounce';

type Props = {
  readonly initialConfigs: ClassificationConfig[];
};

const CATEGORIES = ['Content', 'Actor'] as const;

// Toggle-only view of the taxonomy tree: show or hide a classification from analysts, nothing
// else. Renaming, restricting, adding and deleting stay in the full editor (ClassificationsAdmin,
// admin-only) - this is what /admin renders for analyst_admin instead.
export function ClassificationVisibilityAdmin({initialConfigs}: Props) {
  const [configs, setConfigs] = useState<ClassificationConfig[]>(initialConfigs);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('Content');
  const [search, setSearch] = useState('');
  const query = useDebounce(search, 300);
  // Every policy violation starts expanded, matching the full editor's tree - collapse just hides
  // risks a viewer isn't currently working with, it never affects what's visible to analysts.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const hiddenMut = useSetClassificationHidden();

  const tree = useMemo(() => {
    const childrenByParent = new Map<string, ClassificationConfig[]>();

    for (const config of configs) {
      if (config.parent_id && config.categories.includes(category)) {
        const children = childrenByParent.get(config.parent_id) ?? [];
        children.push(config);
        childrenByParent.set(config.parent_id, children);
      }
    }

    for (const children of childrenByParent.values()) {
      children.sort((a, b) => a.sort_order - b.sort_order);
    }

    const allVerticals = configs
      .filter((c) => c.parent_id === null && c.categories.includes(category))
      .sort((a, b) => a.sort_order - b.sort_order);

    // Every descendant of a vertical in tree order, each carrying its depth for indentation. A
    // customer tree is three levels deep, and this view's whole job is toggling visibility, so a
    // level it does not render is a level nobody can hide.
    const descendantsOf = (vertical: ClassificationConfig) => {
      const rows: Array<{config: ClassificationConfig; depth: number; ancestorHidden: boolean}> =
        [];
      const walk = (parentId: string, depth: number, ancestorHidden: boolean) => {
        for (const child of childrenByParent.get(parentId) ?? []) {
          rows.push({config: child, depth, ancestorHidden});
          walk(child.id, depth + 1, ancestorHidden || child.hidden_facets.includes(category));
        }
      };

      walk(vertical.id, 1, vertical.hidden_facets.includes(category));
      return rows;
    };

    const rowsByVertical = new Map(allVerticals.map((v) => [v.id, descendantsOf(v)]));

    // A vertical matches on its own name, or by keeping only the descendants under it that match -
    // a search for a risk name should still surface its vertical, just trimmed to the hits.
    // Collapsed state is ignored here and applied at render time, so a search still finds a match
    // hidden under a collapsed vertical rather than silently missing it.
    const q = query.trim().toLowerCase();
    if (!q) {
      return {verticals: allVerticals, rowsByVertical, displayRowsByVertical: rowsByVertical};
    }

    const verticals: ClassificationConfig[] = [];
    const displayRowsByVertical = new Map<
      string,
      Array<{config: ClassificationConfig; depth: number; ancestorHidden: boolean}>
    >();

    for (const vertical of allVerticals) {
      const rows = rowsByVertical.get(vertical.id) ?? [];
      const verticalMatches = vertical.display_name.toLowerCase().includes(q);
      const matching = rows.filter((r) => r.config.display_name.toLowerCase().includes(q));

      if (verticalMatches || matching.length > 0) {
        verticals.push(vertical);
        displayRowsByVertical.set(vertical.id, verticalMatches ? rows : matching);
      }
    }

    return {verticals, rowsByVertical, displayRowsByVertical};
  }, [configs, category, query]);

  const handleToggleHidden = async (id: string, hidden: boolean) => {
    let updated;
    try {
      updated = await hiddenMut.mutateAsync({id, facet: category, hidden});
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to update visibility.'), 'error');
      return;
    }

    const byId = new Map(updated.map((c) => [c.id, c]));
    setConfigs((prev) => prev.map((c) => byId.get(c.id) ?? c));
  };

  const toggleCollapsed = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* The section's name is already the page header's title, so only its hint is repeated. */}
      <p className="text-muted-foreground text-xs">{adminSection('classifications')?.hint}</p>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <ToggleGroup
          variant="outline"
          spacing={0}
          size="sm"
          aria-label="Taxonomy"
          value={[category]}
          onValueChange={(next) => {
            const [selected] = next as Array<(typeof CATEGORIES)[number]>;
            if (selected) {
              setCategory(selected);
            }
          }}>
          {CATEGORIES.map((c) => (
            <ToggleGroupItem key={c} value={c}>
              {c} Classifications
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <InputGroup className="w-56 max-w-full">
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            aria-label="Search classifications"
            placeholder="Search classifications…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </InputGroup>
      </div>

      <TablePanel>
        {tree.verticals.length > 0 ? (
          tree.verticals.map((vertical) => {
            const rows = tree.rowsByVertical.get(vertical.id) ?? [];
            const displayRows = tree.displayRowsByVertical.get(vertical.id) ?? [];
            // A search match forces its vertical open, even if the viewer collapsed it earlier -
            const expanded = query.trim() !== '' || !collapsed.has(vertical.id);

            return (
              <div key={vertical.id} className="border-b last:border-b-0">
                <VisibilityRow
                  bold
                  item={vertical}
                  category={category}
                  hasChildren={rows.length > 0}
                  expanded={expanded}
                  childCount={rows.length}
                  onToggle={handleToggleHidden}
                  onToggleExpanded={() => {
                    toggleCollapsed(vertical.id);
                  }}
                />
                {expanded
                  ? displayRows.map((row) => (
                      <VisibilityRow
                        key={row.config.id}
                        depth={row.depth}
                        item={row.config}
                        category={category}
                        expanded={false}
                        disabled={row.ancestorHidden}
                        onToggle={handleToggleHidden}
                      />
                    ))
                  : null}
              </div>
            );
          })
        ) : query.trim() ? (
          <Empty className="py-10">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Search />
              </EmptyMedia>
              <EmptyTitle>No matches</EmptyTitle>
              <EmptyDescription>
                No classifications match &quot;{query.trim()}&quot;.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : null}
      </TablePanel>
    </div>
  );
}

function VisibilityRow({
  item,
  category,
  bold,
  depth,
  hasChildren,
  childCount,
  expanded,
  disabled,
  onToggle,
  onToggleExpanded
}: {
  readonly item: ClassificationConfig;
  readonly category: 'Actor' | 'Content';
  readonly bold?: boolean;
  readonly depth?: number;
  readonly hasChildren?: boolean;
  readonly childCount?: number;
  readonly expanded: boolean;
  readonly disabled?: boolean;
  readonly onToggle: (id: string, hidden: boolean) => Promise<void>;
  readonly onToggleExpanded?: () => void;
}) {
  const hidden = disabled || item.hidden_facets.includes(category);

  return (
    <div
      className="flex items-center gap-3 py-2 pr-3"
      style={{paddingLeft: `${0.5 + (depth ?? 0) * 1.5}rem`}}>
      {bold ? (
        hasChildren ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${item.display_name}` : `Expand ${item.display_name}`}
            onClick={onToggleExpanded}>
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </Button>
        ) : (
          <div className="size-7 shrink-0" />
        )
      ) : null}
      {/* A hidden row is struck through rather than tinted: a fill on a row reads as selection,
          and dimming the whole row would make its switch look disabled when it is not. */}
      <span className={cn('flex-1', bold && 'font-medium', hidden && 'line-through opacity-50')}>
        {item.display_name}
      </span>
      {item.is_potential_p0 ? (
        <Tooltip>
          <TooltipTrigger render={<span className="inline-flex" />}>
            <MailWarning className="text-destructive-ink size-3" aria-label="Potential P0" />
          </TooltipTrigger>
          <TooltipContent>Potential P0</TooltipContent>
        </Tooltip>
      ) : null}
      {hasChildren && !expanded ? (
        <span className="text-muted-foreground text-xs">{childCount} items</span>
      ) : null}
      <Badge variant={hidden ? 'outline' : 'secondary'}>
        {hidden ? <EyeOff /> : <Eye />}
        {hidden ? 'Hidden' : 'Visible'}
      </Badge>
      <Switch
        size="sm"
        checked={!hidden}
        aria-label={
          hidden
            ? `Show ${item.display_name} to analysts`
            : `Hide ${item.display_name} from analysts`
        }
        {...(disabled === undefined ? {} : {disabled})}
        onCheckedChange={() => {
          void onToggle(item.id, !hidden);
        }}
      />
    </div>
  );
}
